import {
  Box,
  BoxProps,
  Typography,
  alpha,
  styled,
  useTheme,
} from "@mui/material";
import { Line } from "react-chartjs-2";
import { formatDisplayTypeValue, formatMonthYear, isDefined } from "@app/utils";
import { chartsColor } from "@app/theme";
import { ReactElement, useCallback, useEffect, useRef } from "react";
import {
  ChartTooltip,
  RenderTooltip,
  useChartTooltip,
} from "@app/components/ChartTooltip";
import { ChartPlugins, ChartUtils } from "@app/utils/chartUtils";
import { CarChartLegend } from "@app/components/ChartLegend";
import { SimulationModelsHistoryItem } from "./useSimulationModelsHistory";
import { ChartDataset } from "chart.js";
import { useChartHighlight } from "@app/hooks/useChartHighlight";
import { ChartJSOrUndefined } from "react-chartjs-2/dist/types";
import { PointPrefixedOptions } from "chart.js";

const chartHeight = 456;

type SimulationModelsHistoryChartDataset = ChartDataset<
  "line",
  { x: string; y: number }[]
> & { color: string };

export const getSimulationModelsHistoryChartData = (
  items: SimulationModelsHistoryItem[],
  hiddenFields: string[],
): {
  datasets: SimulationModelsHistoryChartDataset[];
  yMin: number;
  yMax: number;
  y1Min: number;
  y1Max: number;
} => {
  return {
    datasets: [
      {
        id: "static-mu",
        label: "Static Mu",
        color: chartsColor.green,
        data: items.map((i) => ({
          x: i.date,
          y: i.staticMu,
        })),
        hidden: hiddenFields.includes("mu"),
      },
      {
        id: "static-sigma",
        label: "Static Sigma",
        color: chartsColor.orange,
        yAxisID: "y1",
        data: items.map((i) => ({
          x: i.date,
          y: i.staticSigma,
        })),
        hidden: hiddenFields.includes("sigma"),
      },
      {
        id: "dynamic-mu",
        label: "Dynamic Mu",
        color: chartsColor.blue,
        data: items.map((i) => ({
          x: i.date,
          y: i.dynamicMu,
        })),
        hidden: hiddenFields.includes("mu"),
      },
      {
        id: "dynamic-sigma",
        label: "Dynamic Sigma",
        color: chartsColor.purple,
        yAxisID: "y1",
        data: items.map((i) => ({
          x: i.date,
          y: i.dynamicSigma,
        })),
        hidden: hiddenFields.includes("sigma"),
      },
    ],
    yMin: 0,

    yMax: Math.max(...items.map((i) => i.staticMu)),
    y1Min: 0,
    y1Max: Math.max(...items.map((i) => i.staticMu)),
  };
};

export interface SimulationModelsHistoryChartProps {
  sx?: BoxProps["sx"];
  items: SimulationModelsHistoryItem[];
  isWhiteContext?: boolean;
  renderAxisControls?: () => ReactElement;
  hiddenFields: string[];
}

const StyledLine = styled(Line)(({ theme }) => ({
  width: "100%",
  height: chartHeight,
  maxHeight: chartHeight,
})) as typeof Line;

type ChartRef = ChartJSOrUndefined<
  "line",
  {
    x: string;
    y: number;
  }[],
  unknown
>;

export const SimulationModelsHistoryChart = (
  props: SimulationModelsHistoryChartProps,
) => {
  const chartHighlight = useChartHighlight();
  const { datasets } = getSimulationModelsHistoryChartData(
    props.items,
    props.hiddenFields,
  );

  const { tooltipPlugin, tooltipData } = useChartTooltip({
    chartHighlight,
  });

  const chartRef = useRef<ChartRef | null>();

  const chartRefCallback = useCallback(
    (value: ChartRef | null) => {
      chartHighlight.unsubscribe(chartRef.current);
      chartRef.current = value;

      chartHighlight.subscribe(chartRef.current, (hlValue) => {
        if (isDefined(hlValue.dataIndex)) {
          return; // do not highlight if hover over line
        }

        const chart = chartRef.current;
        if (!chart) {
          return;
        }

        const hoverDatasetIndex = hlValue.datasetIndex ?? -1;

        chart.config.data.datasets.forEach((i, idx) => {
          const ds = i as typeof i & PointPrefixedOptions & { color: string };

          if (hoverDatasetIndex < 0) {
            ds.borderWidth = 2;
            ds.pointRadius = 2;
            ds.order = 0;

            ds.borderColor = ds.color;
            ds.pointBorderColor = ds.color;
            ds.pointBackgroundColor = ds.color;
          } else if (idx === hoverDatasetIndex) {
            ds.borderWidth = 2;
            ds.pointRadius = 2;
            ds.order = 0;
          } else {
            ds.borderWidth = 1;
            ds.pointRadius = 0;
            ds.order = 10;

            const lineColor = alpha(ds.color, 0.4);

            ds.borderColor = lineColor;
            ds.pointBorderColor = lineColor;
            ds.pointBackgroundColor = lineColor;
          }
        });

        chart.update();

        chartRef.current?.update();
      });
    },
    [chartHighlight],
  );

  const theme = useTheme();

  const yFormatter = formatDisplayTypeValue("PERCENTAGE");

  const formatYValue = (value: number | string) =>
    typeof value === "number" ? yFormatter(value) : "";

  const renderTooltip: RenderTooltip = (datasetIndex, dataIndex) => {
    const ds = datasets[datasetIndex];
    const isStatic = datasetIndex < 2;
    if (!ds) {
      return <></>;
    }

    const xValue = ds.data?.[dataIndex]?.x ?? 0;

    let yDs: SimulationModelsHistoryChartDataset;
    let y1Ds: SimulationModelsHistoryChartDataset;

    if (isStatic) {
      yDs = datasets[0];
      y1Ds = datasets[1];
    } else {
      yDs = datasets[2];
      y1Ds = datasets[3];
    }

    const yValue = yDs.data?.[dataIndex]?.y ?? 0;
    const y1Value = y1Ds.data?.[dataIndex]?.y ?? 0;

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography variant="par01SemiBold" sx={{ fontSize: 15, mb: 1 }}>
          {`${isStatic ? "Static" : "Dynamic"} - ${formatMonthYear(xValue)}`}
        </Typography>
        <Typography variant="par01Regular" sx={{ fontSize: 13 }}>
          <Box
            sx={{
              mb: -0.25,
              mr: 0.5,
              width: 13,
              height: 13,
              border: "1px solid",
              borderColor: "gray6",
              borderRadius: "3px",
              backgroundColor: yDs.color,
              display: "inline-block",
            }}
          />
          {`Mu: ${formatYValue(yValue)}`}
        </Typography>
        <Typography variant="par01Regular" sx={{ fontSize: 13 }}>
          <Box
            sx={{
              mb: -0.25,
              mr: 0.5,
              width: 13,
              height: 13,
              border: "1px solid",
              borderColor: "gray6",
              borderRadius: "3px",
              backgroundColor: y1Ds.color,
              display: "inline-block",
            }}
          />
          {`Sigma: ${formatYValue(y1Value)}`}
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <ChartTooltip
        tooltipData={tooltipData}
        renderTooltip={renderTooltip}
        sx={{
          ...props.sx,
        }}
      >
        <StyledLine
          ref={chartRefCallback}
          datasetIdKey="id"
          data={{
            datasets: datasets.map((ds) => ({
              ...ds,
              borderColor: ds.color,
              backgroundColor: ds.color,
              pointBackgroundColor: ds.color,
              pointBorderColor: ds.color,
              borderWidth: 2,
              pointRadius: 2,
            })),
          }}
          options={{
            maintainAspectRatio: false,
            scales: {
              x: {
                type: "time",
                ticks: {
                  font: {
                    family: theme.typography.fontFamily,
                    size: 13,
                    weight: 600,
                  },
                },
                time: {
                  unit: "year",
                },
                grid: {
                  color: ChartUtils.gridColor,
                  lineWidth: 2,
                  tickColor: theme.palette.white,
                },
                title: {
                  display: false,
                },
              },
              y: {
                offset: true,
                ticks: {
                  font: {
                    family: theme.typography.fontFamily,
                    size: 15,
                    weight: 600,
                  },
                  callback: formatYValue,
                },
                grid: {
                  color: ChartUtils.gridColor,
                  lineWidth: 2,
                  tickColor: theme.palette.white,
                },
                title: {
                  display: true,
                  text: "Mu",
                  color: theme.palette.softBlack,
                  font: {
                    family: theme.typography.fontFamily,
                    size: 15,
                    weight: 600,
                    lineHeight: 1,
                  },
                  padding: {
                    bottom: 20,
                  },
                },
              },
              y1: {
                offset: true,
                position: "right",
                ticks: {
                  font: {
                    family: theme.typography.fontFamily,
                    size: 15,
                    weight: 600,
                  },
                  callback: formatYValue,
                },
                grid: {
                  display: false,
                },
                title: {
                  display: true,
                  text: "Sigma",
                  color: theme.palette.softBlack,
                  font: {
                    family: theme.typography.fontFamily,
                    size: 15,
                    weight: 600,
                    lineHeight: 1,
                  },
                  padding: {
                    bottom: 20,
                  },
                },
              },
            },
            plugins: {
              tooltip: tooltipPlugin,
              roundedBackground: {
                contextColor: props.isWhiteContext
                  ? theme.palette.white
                  : theme.palette.gray1,
                backgroundColor: props.isWhiteContext
                  ? theme.palette.gray1
                  : theme.palette.white,
                borderColor: theme.palette.gray7,
                borderRadius: 5,
              },
            },
          }}
          plugins={[ChartPlugins.roundedBackground]}
        />
      </ChartTooltip>
      <CarChartLegend
        sx={{ mt: 5, ml: 9, minHeight: 38 }}
        items={datasets
          .filter((i) => !i.hidden)
          .map((ds, idx) => ({
            label: ds.label,
            color: ds.color,
            datasetIndex: idx,
            // dataIndex: 0,
          }))}
        chartHighlight={chartHighlight}
      />
    </Box>
  );
};
